<template>
  <div class="excel-container">
    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="left-buttons">
        <el-button type="primary" @click="backToUpload">
          <i class="el-icon-back"></i> 返回首頁
        </el-button>
        <el-upload class="upload-btn" action="#" :auto-upload="false" :show-file-list="false"
          accept="image/*,application/pdf" :on-change="handleFileUpload">
          <el-button type="primary">
            <i class="el-icon-upload2"></i> 上傳文件
          </el-button>
        </el-upload>

        <!-- 阈值设置 -->
        <div class="threshold-setting">
          <label class="threshold-label">ROI:</label>
          <el-input-number v-model="threshold" :min="0" :max="1" :step="0.01" :precision="2" controls-position="right"
            class="threshold-input" size="small" />
        </div>

      </div>
      <div class="right-buttons">
        <el-button type="info" :disabled="!fileUrls.htmlUrl" @click="previewHtml">
          <i class="el-icon-view"></i> 預覽HTML
        </el-button>
        <el-button type="warning" :disabled="!fileUrls.markdownUrl" @click="previewMarkdown">
          <i class="el-icon-edit"></i> 預覽Markdown
        </el-button>
        <el-button type="info" plain :disabled="!fileUrls.htmlUrl" @click="downloadHtml">
          <i class="el-icon-download"></i> 下載HTML
        </el-button>
        <el-button type="warning" plain :disabled="!fileUrls.markdownUrl" @click="downloadMarkdown">
          <i class="el-icon-download"></i> 下載Markdown
        </el-button>
        <el-button type="success" :loading="saving" :disabled="!fileData || saving" @click="saveExcel">
          <i class="el-icon-download"></i> {{ saving ? '保存中...' : '保存Excel' }}
        </el-button>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area">
      <!-- 左侧文件预览区域 -->
      <div class="preview-panel" v-if="imageUrl || pdfUrl">
        <!-- 图片预览 -->
        <div v-if="fileType === 'image'" class="image-container">
          <img :src="imageUrl" alt="上傳的圖片" class="uploaded-image" @click="showImagePreview = true" title="點擊放大預覽" />
        </div>

        <!-- PDF预览 -->
        <div v-if="fileType === 'pdf'" class="pdf-container">
          <my-pdf-view :url="pdfUrl" @mountedLoaded="onPdfLoaded" @pageChange="onPdfPageChange" />
        </div>
      </div>

      <!-- 右侧Excel编辑区域 -->
      <div :class="['excel-panel', { 'full-width': !imageUrl && !pdfUrl }]">
        <div id="luckysheet"></div>
      </div>
    </div>

    <!-- 图片预览组件 -->
    <image-preview :visible.sync="showImagePreview" :image-url="imageUrl" />

    <!-- HTML预览编辑组件 -->
    <html-preview-editor :visible.sync="showHtmlDialog" :file-url="fileUrls.htmlUrl"
      @content-changed="onHtmlContentChanged" />

    <!-- Markdown预览编辑组件 -->
    <markdown-preview-editor :visible.sync="showMarkdownDialog" :file-url="fileUrls.markdownUrl"
      @content-changed="onMarkdownContentChanged" />

  </div>
</template>

<script>
// LuckyExcel 通过 CDN 引入，使用全局变量 LuckyExcel
import { exportExcel } from './export'
import { Message, Loading } from 'element-ui'
import { imageToExcel, downloadExcelFile, downloadFile } from '@/api/excel'
import ImagePreview from './ImagePreview.vue'
import HtmlPreviewEditor from './HtmlPreviewEditor.vue'
import MarkdownPreviewEditor from './MarkdownPreviewEditor.vue'
import MyPdfView from './myPdfView.vue'

export default {
  name: 'ExcelViewer',
  components: {
    ImagePreview,
    HtmlPreviewEditor,
    MarkdownPreviewEditor,
    MyPdfView
  },
  data() {
    return {
      fileData: null,
      luckysheet: null,
      saving: false,
      imageUrl: '',
      pdfUrl: '',
      fileType: '', // 'image' 或 'pdf'
      showImagePreview: false,
      threshold: 0.5,  // 默认阈值0.5
      // 存储API返回的文件URLs
      fileUrls: {
        excelUrl: '',
        htmlUrl: '',
        markdownUrl: ''
      },
      // 简单缓存
      _excelCache: new Map(),
      _isInitialized: false,
      // 预览对话框状态
      showHtmlDialog: false,
      showMarkdownDialog: false
    }
  },
  mounted() {
    this.initLuckySheet()
  },
  // 对外暴露方法
  provide() {
    return {
      openExcelData: this.openExcelData,
      openExcelFromBase64: this.openExcelFromBase64 // 保持向后兼容
    }
  },
  methods: {
    // 返回上传页面
    backToUpload() {
      this.$router.push('/table');
    },

    // 处理文件上传（支持图片和PDF预览）
    async handleFileUpload(file) {
      if (!file) return;

      // 检测文件类型
      const fileType = this.detectFileType(file.raw);
      this.fileType = fileType;

      if (fileType === 'pdf') {
        // 处理PDF文件，也调用API
        this.handlePdfUpload(file);
        return;
      } else if (fileType === 'image') {
        // 处理图片文件，保持原有的图片转Excel功能
        this.handleImageChange(file);
        return;
      } else {
        Message.warning('不支持的文件格式，請上傳圖片或PDF文件');
        return;
      }
    },

    // 处理图片上传（并行优化版）
    async handleImageChange(file) {
      if (!file) return;

      const loadingInstance = Loading.service({
        lock: true,
        text: '正在處理圖片，請稍候...',
        background: 'rgba(255, 255, 255, 0.9)'
      });

      try {
        // 立即显示图片，不等待API响应
        this.imageUrl = URL.createObjectURL(file.raw);
        this.pdfUrl = '';
        this.fileType = 'image';

        // 并行处理：API调用和预初始化LuckySheet
        const [response] = await Promise.all([
          imageToExcel(file.raw, this.threshold * 100),
          // 预热LuckySheet实例
          new Promise(resolve => {
            this.$nextTick(() => {
              if (!window.luckysheet || !document.getElementById('luckysheet')) {
                this.initLuckySheet();
              }
              resolve();
            });
          })
        ]);

        if (response.code === 200 && response.data && response.data.excelUrl) {
          // 保存所有文件URLs
          this.fileUrls = {
            excelUrl: response.data.excelUrl || '',
            htmlUrl: response.data.htmlUrl || '',
            markdownUrl: response.data.markdownUrl || ''
          };

          // 并行处理：下载Excel和更新UI状态
          const [excelBlob] = await Promise.all([
            downloadExcelFile(response.data.excelUrl),
            // 更新loading文本
            new Promise(resolve => {
              loadingInstance.setText('正在載入Excel數據...');
              setTimeout(resolve, 100);
            })
          ]);

          // 快速处理Excel数据
          await this.processExcelDataFast(excelBlob);
          this.fileData = await excelBlob.arrayBuffer();

          loadingInstance.close();
          Message.success('圖片轉換成功！');
        } else {
          throw new Error(response.msg || '轉換失敗');
        }
      } catch (error) {
        console.error('圖片處理失敗:', error);
        loadingInstance.close();
        Message.error(error.message || '圖片處理失敗，請稍後重試');
      }
    },

    // 初始化 Luckysheet（轻量级版本）
    initLuckySheet() {
      this.$nextTick(() => {
        // 检查是否已经初始化
        const container = document.getElementById('luckysheet');
        if (!container) return;

        // 先销毁现有实例
        if (window.luckysheet) {
          window.luckysheet.destroy();
        }

        // 轻量级初始配置
        const options = {
          container: 'luckysheet',
          lang: 'zh',
          showinfobar: false,
          plugins: [], // 不加载插件
          forceCalculation: false,
          // 最小数据集
          data: [{
            name: "準備中...",
            color: "",
            status: 1,
            order: 0,
            data: [], // 空数据
            config: {},
            index: 0
          }],
          // 性能优化
          showToolbar: false, // 初始隐藏工具栏
          showInfoBar: false,
          showFormulaBar: false,
          allowEdit: false, // 初始禁用编辑
          showConfigWindowResize: false
        };

        // 创建新实例
        this.luckysheet = window.luckysheet.create(options);
      });
    },

    // 处理文件上传
    async handleFileChange(file) {
      if (!file) return

      try {
        const fileData = await this.readFile(file.raw)
        this.fileData = fileData

        // 使用 LuckyExcel 转换并加载数据
        window.LuckyExcel.transformExcelToLucky(fileData, (exportJson, luckysheetfile) => {
          if (exportJson.sheets === null || exportJson.sheets.length === 0) {
            Message.warning('Excel文件為空')
            return
          }

          // 先销毁现有实例
          if (window.luckysheet) {
            window.luckysheet.destroy();
          }

          // 确保每个工作表都有唯一的索引
          exportJson.sheets.forEach((sheet, index) => {
            sheet.index = index;
            sheet.status = index === 0 ? 1 : 0;
            sheet.order = index;
          });

          // 创建新实例
          window.luckysheet.create({
            container: 'luckysheet',
            showinfobar: false,
            lang: 'zh',
            plugins: ['chart'],
            forceCalculation: false,
            data: exportJson.sheets,
            title: file.name
          });

          Message.success('Excel導入成功')
        })
      } catch (error) {
        console.error('Excel解析失敗:', error)
        Message.error('Excel解析失敗，請檢查文件格式')
      }
    },

    // 读取文件
    readFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e)
        reader.readAsArrayBuffer(file)
      })
    },

    // 保存Excel
    async saveExcel() {
      try {
        this.$set(this, 'saving', true);

        // 强制完成当前单元格的编辑
        window.luckysheet.exitEditMode();

        // 确保所有编辑都被提交到内存中
        await new Promise(resolve => setTimeout(resolve, 50));

        // 获取所有工作表的数据
        const allSheets = window.luckysheet.getluckysheetfile();

        // 确保获取最新的数据，包括当前编辑的单元格
        window.luckysheet.refresh();

        const exportData = allSheets.map((sheet, index) => {
          // 获取当前sheet的数据
          const sheetData = sheet.data || window.luckysheet.getSheetData(sheet.index);

          return {
            name: sheet.name || `Sheet${index + 1}`,
            color: sheet.color || '',
            status: sheet.status,
            order: sheet.order,
            data: sheetData,
            config: {
              merge: sheet.config?.merge || {},
              borderInfo: sheet.config?.borderInfo || [],
              columnlen: sheet.config?.columnlen || {},
              rowlen: sheet.config?.rowlen || {},
              customWidth: sheet.config?.customWidth || 0,
              customHeight: sheet.config?.customHeight || 0,
              rowhidden: sheet.config?.rowhidden || {},
              colhidden: sheet.config?.colhidden || {},
              borderinfo: sheet.config?.borderinfo || [],
              authority: sheet.config?.authority || {}
            },
            index: sheet.index,
            // 添加必要的属性
            celldata: sheet.celldata || [],
            scrollLeft: sheet.scrollLeft || 0,
            scrollTop: sheet.scrollTop || 0,
            zoomRatio: sheet.zoomRatio || 1,
            showGridLines: sheet.showGridLines !== false,
            defaultColWidth: sheet.defaultColWidth || 73,
            defaultRowHeight: sheet.defaultRowHeight || 19,
          };
        });

        // 使用export.js中的exportExcel函数
        const fileName = `excel_${new Date().getTime()}`;
        await new Promise((resolve) => {
          setTimeout(() => {
            // 确保数据中至少包含一个工作表
            if (!exportData.length) {
              exportData.push({
                name: "Sheet1",
                color: "",
                status: 1,
                order: 0,
                data: [],
                config: {},
                index: 0
              });
            }
            exportExcel(exportData, fileName);
            resolve();
          }, 100);
        });

        this.$set(this, 'saving', false);
        Message.success('Excel保存成功');
      } catch (error) {
        console.error('Excel保存失敗:', error);
        this.$set(this, 'saving', false);
        Message.error('Excel保存失敗，請稍後重試');
      }
    },

    // 打开Excel数据方法（支持base64字符串、ArrayBuffer或Blob）
    openExcelData(excelData) {
      if (!excelData) {
        Message.error('無效的Excel數據')
        return
      }

      try {
        this.processExcelData(excelData)
      } catch (error) {
        console.error('Excel數據解析失敗:', error)
        Message.error('Excel數據解析失敗，請檢查格式')
      }
    },

    // 将base64字符串转换为Blob
    base64ToBlob(base64String) {
      const base64Data = base64String.replace(/^data:.*?;base64,/, '')
      const binaryString = atob(base64Data)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    },

    // 快速Excel数据处理方法
    async processExcelDataFast(excelData) {
      return new Promise(async (resolve, reject) => {
        try {
          let arrayBuffer;

          // 优化数据转换
          if (excelData instanceof ArrayBuffer) {
            arrayBuffer = excelData;
          } else if (excelData instanceof Blob) {
            arrayBuffer = await excelData.arrayBuffer();
          } else if (typeof excelData === 'string') {
            const blob = this.base64ToBlob(excelData);
            arrayBuffer = await blob.arrayBuffer();
          } else {
            throw new Error('不支持的數據格式');
          }

          // 使用 LuckyExcel 转换（优化配置）
          window.LuckyExcel.transformExcelToLucky(arrayBuffer, (exportJson, luckysheetfile) => {
            try {
              if (exportJson.sheets === null || exportJson.sheets.length === 0) {
                reject(new Error('Excel文件為空'));
                return;
              }

              // 快速销毁和重建
              if (window.luckysheet) {
                window.luckysheet.destroy();
              }

              // 最小化配置，专注性能
              const config = {
                container: 'luckysheet',
                showinfobar: false,
                lang: 'zh',
                data: exportJson.sheets,
                title: 'Excel編輯',
                // 性能优化配置
                forceCalculation: false,
                plugins: [],
                allowCopy: true,
                allowEdit: true,
                // 减少不必要的功能
                showToolbar: true,
                showInfoBar: false,
                showFormulaBar: true,
                showConfigWindowResize: false
              };

              window.luckysheet.create(config);
              this.fileData = arrayBuffer;
              resolve();
            } catch (error) {
              reject(error);
            }
          });
        } catch (error) {
          reject(error);
        }
      });
    },

    // 保持原方法兼容性
    async processExcelData(excelData) {
      return this.processExcelDataFast(excelData);
    },

    // 保持向后兼容的base64方法
    openExcelFromBase64(base64String) {
      this.openExcelData(base64String)
    },

    // 下载HTML文件
    async downloadHtml() {
      if (!this.fileUrls.htmlUrl) {
        Message.warning('HTML文件不可用')
        return
      }

      try {
        // 从URL中提取文件名
        const fileName = this.fileUrls.htmlUrl.split('/').pop() || 'document.html'
        await downloadFile(this.fileUrls.htmlUrl, fileName)
        Message.success('HTML文件下載成功')
      } catch (error) {
        console.error('HTML下載失敗:', error)
        Message.error('HTML文件下載失敗，請稍後重試')
      }
    },

    // 下载Markdown文件
    async downloadMarkdown() {
      if (!this.fileUrls.markdownUrl) {
        Message.warning('Markdown文件不可用')
        return
      }

      try {
        // 从URL中提取文件名
        const fileName = this.fileUrls.markdownUrl.split('/').pop() || 'document.md'
        await downloadFile(this.fileUrls.markdownUrl, fileName)
        Message.success('Markdown文件下載成功')
      } catch (error) {
        console.error('Markdown下載失敗:', error)
        Message.error('Markdown文件下載失敗，請稍後重試')
      }
    },

    // 预览HTML文件
    previewHtml() {
      if (!this.fileUrls.htmlUrl) {
        Message.warning('HTML文件不可用')
        return
      }
      this.showHtmlDialog = true
    },

    // 预览Markdown文件
    previewMarkdown() {
      if (!this.fileUrls.markdownUrl) {
        Message.warning('Markdown文件不可用')
        return
      }
      this.showMarkdownDialog = true
    },

    // HTML内容变化事件处理
    onHtmlContentChanged(content) {
      console.log('HTML content changed:', content.length, 'characters')
    },

    // Markdown内容变化事件处理
    onMarkdownContentChanged(content) {
      console.log('Markdown content changed:', content.length, 'characters')
    },

    // 检测文件类型
    detectFileType(file) {
      const fileType = file.type;
      if (fileType === 'application/pdf') {
        return 'pdf';
      } else if (fileType.startsWith('image/')) {
        return 'image';
      }
      return 'unknown';
    },

    // PDF加载完成事件
    onPdfLoaded(totalPages) {
      console.log('PDF加載完成，總頁數:', totalPages);
    },

    // PDF翻页事件
    onPdfPageChange(page) {
      console.log('PDF翻到第', page, '頁');
    },

    // 处理PDF上传（使用相同的API接口）
    async handlePdfUpload(file) {
      if (!file) return;

      const loadingInstance = Loading.service({
        lock: true,
        text: '正在處理PDF文件，請稍候...',
        background: 'rgba(255, 255, 255, 0.9)'
      });

      try {
        // 立即显示PDF预览，不等待API响应
        this.pdfUrl = URL.createObjectURL(file.raw);
        this.imageUrl = '';

        // 并行处理：API调用和预初始化LuckySheet
        const [response] = await Promise.all([
          imageToExcel(file.raw, this.threshold * 100), // 使用相同的API
          // 预热LuckySheet实例
          new Promise(resolve => {
            this.$nextTick(() => {
              if (!window.luckysheet || !document.getElementById('luckysheet')) {
                this.initLuckySheet();
              }
              resolve();
            });
          })
        ]);

        if (response.code === 200 && response.data && response.data.excelUrl) {
          // 保存所有文件URLs
          this.fileUrls = {
            excelUrl: response.data.excelUrl || '',
            htmlUrl: response.data.htmlUrl || '',
            markdownUrl: response.data.markdownUrl || ''
          };

          // 并行处理：下载Excel和更新UI状态
          const [excelBlob] = await Promise.all([
            downloadExcelFile(response.data.excelUrl),
            // 更新loading文本
            new Promise(resolve => {
              loadingInstance.setText('正在載入Excel數據...');
              setTimeout(resolve, 100);
            })
          ]);

          // 快速处理Excel数据
          await this.processExcelDataFast(excelBlob);
          this.fileData = await excelBlob.arrayBuffer();

          loadingInstance.close();
          Message.success('PDF轉換成功！');
        } else {
          throw new Error(response.msg || '轉換失敗');
        }
      } catch (error) {
        console.error('PDF處理失敗:', error);
        loadingInstance.close();
        Message.error('PDF處理失敗，請稍後重試');
      }
    },


  }
}
</script>

<style scoped>
.excel-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.toolbar {
  padding: 10px 20px;
  background: #f8fafc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e6e6e6;
  height: 60px;
  flex-shrink: 0;
}

.left-buttons,
.right-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.content-area {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.preview-panel {
  width: 600px;
  padding: 20px;
  background: #f8fafc;
  border-right: 1px solid #e6e6e6;
  overflow: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.pdf-container {
  width: 100%;
  height: calc(100vh - 140px);
  /* 减去工具栏和边距的高度 */
  display: flex;
  flex-direction: column;
}

.uploaded-image {
  width: 100%;
  height: auto;
  max-height: calc(100vh - 100px);
  object-fit: contain;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.uploaded-image:hover {
  transform: scale(1.03);
  box-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.2);
}

.excel-panel {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.excel-panel.full-width {
  width: 100%;
}

#luckysheet {
  width: 100%;
  height: 100%;
}

.upload-btn {
  display: inline-block;
}

.el-button [class^="el-icon-"] {
  margin-right: 4px;
}

/* 加载对话框样式 */
.loading-dialog {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

.loading-dialog .el-dialog__header {
  display: none;
}

.loading-dialog .el-dialog__body {
  padding: 40px 20px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.loading-icon {
  width: 60px;
  height: 60px;
  margin-bottom: 20px;
  position: relative;
}

.circular {
  width: 100%;
  height: 100%;
  animation: loading-rotate 2s linear infinite;
}

.path {
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-width: 4;
  stroke: #409EFF;
  stroke-linecap: round;
  animation: loading-dash 1.5s ease-in-out infinite;
}

.success-icon {
  font-size: 60px;
  color: #67C23A;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 16px;
  color: #606266;
  margin: 0;
  text-align: center;
}

.loading-content.success .loading-text {
  color: #67C23A;
}

@keyframes loading-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes loading-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -40px;
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -120px;
  }
}

/* 阈值设置样式 */
.threshold-setting {
  display: flex;
  align-items: center;
  margin-left: 15px;
  padding: 4px 8px;
  background: #f8fafc;
  border-radius: 4px;
  border: 1px solid #e1e8ed;
}

.threshold-label {
  font-weight: 500;
  color: #2c3e50;
  margin-right: 6px;
  font-size: 12px;
  white-space: nowrap;
}

.threshold-input {
  width: 100px;
}
</style>